import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, GeoSearchResultItem, Locale } from '@smg-real-estate/geo-libs';
import { GeoApiClient } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';

const MAX_AUTOCOMPLETE_RESULT_COUNT = 20;

export interface GeoState {
  geoLocations: GeoLocation[];
  unresolvableGeoIds: string[];
  unresolvableUrlNames: {
    [HgSupportedLanguage.DE]: string[];
    [HgSupportedLanguage.EN]: string[];
    [HgSupportedLanguage.FR]: string[];
    [HgSupportedLanguage.IT]: string[];
  };
  autocompleteResults: GeoSearchResultItem[];
}

let geoApiClient: GeoApiClient;

const getGeoApiClient = (): GeoApiClient => {
  if (!geoApiClient) {
    const apiBaseUrl = import.meta.env.VUE_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      throw new Error('VUE_APP_API_BASE_URL environment variable is not defined');
    }
    const geoApiBaseUrl = `${apiBaseUrl}/geo`;
    geoApiClient = new GeoApiClient(geoApiBaseUrl);
  }
  return geoApiClient;
};

const showTypeLabel = (location: GeoLocation) =>
  ['canton', 'region', 'district'].includes(location.type);

export function renderGeoLocationName(
  location: GeoLocation,
  language: HgSupportedLanguage,
): string {
  const [first, ...rest] = location.names[language];
  return [
    showTypeLabel(location) ? location.typeLabel?.[language] : undefined,
    rest.length > 0 ? `${first} (${rest.join(', ')})` : first,
  ]
    .filter((x) => !!x)
    .join(' ');
}

const filterNonExistingLocationIds = (locationIds: string[], geoLocations: GeoLocation[]) =>
  locationIds.filter(
    (locationId) => !geoLocations.find((geoLocation) => geoLocation.id === locationId),
  );

const filterNonExistingUrlNames = (lang: string, urlNames: string[], geoLocations: GeoLocation[]) =>
  urlNames.filter(
    (urlName) =>
      !geoLocations.find(
        (geoLocation) =>
          geoLocation.urlNames[lang as Locale] === urlName ||
          (geoLocation.is24UrlNames && geoLocation.is24UrlNames[lang as Locale] === urlName),
      ),
  );

function keepNecessaryFields({
  id,
  type,
  names,
  urlNames,
  is24UrlNames,
  parentPaths,
  children,
  typeLabel,
  preposition,
  wikidataId,
  newId,
}: GeoLocation): GeoLocation {
  return {
    id,
    type,
    names,
    urlNames,
    parentPaths,
    children,
    typeLabel,
    preposition,
    wikidataId,
    is24UrlNames,
    newId,
  };
}

export const useGeoStore = defineStore('geo', {
  state: (): GeoState => ({
    geoLocations: [],
    unresolvableGeoIds: [],
    unresolvableUrlNames: {
      [HgSupportedLanguage.DE]: [],
      [HgSupportedLanguage.EN]: [],
      [HgSupportedLanguage.FR]: [],
      [HgSupportedLanguage.IT]: [],
    },
    autocompleteResults: [],
  }),

  getters: {
    getGeoLocationByUrlName: (state) => (urlName: string, language: HgSupportedLanguage) => {
      return state.geoLocations.find((geoLocation: GeoLocation) => {
        return (
          geoLocation.urlNames[language] === urlName ||
          (geoLocation.is24UrlNames && geoLocation.is24UrlNames[language] === urlName)
        );
      });
    },

    getGeoLocationsByUrlName() {
      return (urlNames: string[], language: HgSupportedLanguage) => {
        return urlNames
          .map((urlName) => this.getGeoLocationByUrlName(urlName, language))
          .filter((x): x is GeoLocation => !!x);
      };
    },

    getGeoLocationById: (state) => (id: string) => {
      return state.geoLocations.find(
        (geoLocation) => geoLocation.id === id || geoLocation.newId === id,
      );
    },

    getGeoLocationsById() {
      return (ids: string[]) => {
        return ids.map((id) => this.getGeoLocationById(id)).filter((x): x is GeoLocation => !!x);
      };
    },

    getGeoLocationName() {
      return (id: string, language: HgSupportedLanguage) => {
        const location = this.getGeoLocationById(id);

        if (location) {
          return renderGeoLocationName(location, language);
        }

        return undefined;
      };
    },

    isUnresolvableUrlName: (state) => (urlName: string, language: HgSupportedLanguage) => {
      return state.unresolvableUrlNames[language].indexOf(urlName) >= 0;
    },

    isUnresolvableId: (state) => (id: string) => {
      return state.unresolvableGeoIds.indexOf(id) >= 0;
    },

    /**
     * returns true when for every passed id there was a either a geo location resolved or resolved with an error.
     */
    allGeoLocationsByIdSettled() {
      return (ids: string[]): boolean => {
        const settled = ids
          .map((id) => {
            return !!this.getGeoLocationById(id) || this.isUnresolvableId(id);
          })
          .filter((x) => x);

        return settled.length === ids.length;
      };
    },

    /**
     * returns true when for every passed urlName there was a either a geo location resolved or resolved with an error.
     */
    allGeoLocationsByUrlNameSettled() {
      return (urlNames: string[], language: HgSupportedLanguage): boolean => {
        const settled = urlNames
          .map((urlName) => {
            return (
              !!this.getGeoLocationByUrlName(urlName, language) ||
              this.isUnresolvableUrlName(urlName, language)
            );
          })
          .filter((x) => x);

        return settled.length === urlNames.length;
      };
    },

    getAutocompleteResults: (state) => {
      return state.autocompleteResults ?? [];
    },
  },

  actions: {
    addGeoLocations(geoLocations: GeoLocation[]) {
      const uniqueGeoLocations = [...this.geoLocations, ...geoLocations].filter(
        (g1, i, array) => array.findIndex((g2) => g1.id === g2.id) === i,
      );

      this.geoLocations = uniqueGeoLocations.map(keepNecessaryFields);
    },

    addUnresolvableGeoIds(ids: string[]) {
      this.unresolvableGeoIds = [...this.unresolvableGeoIds, ...ids];
    },

    addUnresolvableUrlNames(language: HgSupportedLanguage, urlNames: string[]) {
      this.unresolvableUrlNames[language] = [...this.unresolvableUrlNames[language], ...urlNames];
    },

    setAutocompleteResults(results: GeoSearchResultItem[]) {
      this.autocompleteResults = results;
    },

    async autocompleteLocations({
      query,
      language,
    }: {
      query: string;
      language: HgSupportedLanguage;
    }) {
      try {
        const geoLocationsSearchResult = await getGeoApiClient().findGeoLocations(
          language,
          query,
          MAX_AUTOCOMPLETE_RESULT_COUNT,
          undefined,
          undefined,
        );
        this.addGeoLocations(geoLocationsSearchResult.results.map((item) => item.geoLocation));
        this.setAutocompleteResults(geoLocationsSearchResult.results);
      } catch (e) {
        console.error(`Cannot load geo autocompletions for lang=${language} query=${query}`, e);
        this.setAutocompleteResults([]);
      }
    },

    /**
     * Function to resolve a list of geo-location-ids to corresponding GeoLocations and to attach them to the store.
     *
     * This function can be called from both server- and client-side, yet the path of action will differ.
     * If locations should be available in the store for the client, the server has to provide those locations
     * by executing this function.
     *
     * Client:
     * When this function gets executed by a client the geo-layer will not be available. The only options then are to
     * either find it in the store - in case it was added during SSR previously - or by requesting the search API.
     *
     * Execution path is as follow:
     * 1) Store: If a locationId is already in store no further actions have to be taken.
     * 2) SearchAPI: Request the search API to resolve locations.
     */
    async resolveLocationsById(geoLocationIds: string[]): Promise<void> {
      // 1) Store
      // Verify if some or all locations are already contained in the store.
      // If so, there is no need to search for them in the lambda-layer or via search API.
      const filteredIds = filterNonExistingLocationIds(geoLocationIds, this.geoLocations);
      if (filteredIds.filter((location) => !!location).length === 0) {
        return;
      }

      // 2) Search API
      // For all locationIds that could not be found in the store the search-API gets requested.
      try {
        const locationsFoundByApi: GeoLocation[] =
          await getGeoApiClient().getGeoLocationsById(filteredIds);
        this.addGeoLocations(locationsFoundByApi);

        const unresolvableIds = filterNonExistingLocationIds(filteredIds, locationsFoundByApi);
        if (unresolvableIds.length > 0) {
          this.addUnresolvableGeoIds(unresolvableIds);
        }
      } catch (error) {
        console.error('Error resolving locations by ID', {
          error,
          locations: filteredIds,
        });
      }
    },

    async resolveLocationsByUrlName(urlNames: string[], language: HgSupportedLanguage) {
      // 1) Store
      // Verify if some or all locations are already contained in the store.
      // If so, there is no need to search for them in the lambda-layer or via search API.
      const filteredUrlNames = filterNonExistingUrlNames(language, urlNames, this.geoLocations);
      if (filteredUrlNames.length === 0) {
        return;
      }

      // 2) Search API
      // For all locationIds that could not be found in the store the search-API gets requested.
      const locationsFoundByApi: GeoLocation[] = (
        await Promise.all(
          filteredUrlNames.map(async (urlName) => {
            try {
              return await getGeoApiClient().getGeoLocationByUrlName(
                urlName,
                language as unknown as Locale,
              );
            } catch (error) {
              console.error('Error resolving location by URL name', { urlName, language, error });
            }
          }),
        )
      ).filter((x): x is GeoLocation => !!x);

      this.addGeoLocations(locationsFoundByApi);

      const unresolvableUrlNames = filterNonExistingUrlNames(
        language,
        filteredUrlNames,
        locationsFoundByApi,
      );
      if (unresolvableUrlNames.length > 0) {
        this.addUnresolvableUrlNames(language, unresolvableUrlNames);
      }
    },
  },
});
